// Query strings for the backend API, as `qs.stringify` writes them (the backend parses them with
// `qs`), without shipping `qs` (40 KB with its dependencies) to the browser. Nested objects and
// arrays use brackets: `ordering[0][field]=createdAt` by default, `tags[]=a` with
// `arrayFormat: "brackets"`. Keys and values are percent-encoded per RFC 3986, brackets included;
// `null` writes an empty value; `undefined`, empty arrays and empty objects are left out; dates are
// ISO strings. `QueryString.test.ts` checks the output against `qs` byte for byte.

export interface StringifyQueryOptions {
  /** `indices` (default): `a[0]=x&a[1]=y`; `brackets`: `a[]=x&a[]=y`. */
  arrayFormat?: "indices" | "brackets";
}

/** RFC 3986 percent-encoding: everything but letters, digits and `-._~`. */
const encode = (value: string): string =>
  encodeURIComponent(value).replace(
    /[!'()*]/g,
    (char) => `%${char.charCodeAt(0).toString(16).toUpperCase()}`,
  );

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value) && !(value instanceof Date);

const appendPairs = (
  pairs: string[],
  prefix: string,
  value: unknown,
  arrayFormat: NonNullable<StringifyQueryOptions["arrayFormat"]>,
) => {
  if (value === undefined || typeof value === "function" || typeof value === "symbol") return;
  if (value === null) {
    pairs.push(`${encode(prefix)}=`);
    return;
  }
  if (value instanceof Date) {
    pairs.push(`${encode(prefix)}=${encode(value.toISOString())}`);
    return;
  }
  if (Array.isArray(value)) {
    for (const [index, item] of value.entries()) {
      const itemPrefix = arrayFormat === "brackets" ? `${prefix}[]` : `${prefix}[${index}]`;
      appendPairs(pairs, itemPrefix, item, arrayFormat);
    }
    return;
  }
  if (isPlainObject(value)) {
    for (const key of Object.keys(value)) {
      appendPairs(pairs, `${prefix}[${key}]`, value[key], arrayFormat);
    }
    return;
  }
  pairs.push(`${encode(prefix)}=${encode(String(value))}`);
};

/** `query` as a query string (no leading `?`), byte-identical to `qs.stringify(query, options)`. */
export const stringifyQuery = (
  query: Record<string, unknown> | null | undefined,
  options?: StringifyQueryOptions,
): string => {
  if (!isPlainObject(query)) return "";
  const pairs: string[] = [];
  for (const key of Object.keys(query)) {
    appendPairs(pairs, key, query[key], options?.arrayFormat ?? "indices");
  }
  return pairs.join("&");
};
